import {Edge, Node} from 'reactflow';

import {assert} from 'Asserts';
import {
  ProcedureEdgeNode,
  ProcedureEdgeType,
  ProcedureType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ExperimentMethod} from 'studies/typed/shared/ExperimentalInputs';
import {
  ConditionGroup,
  getMethodIdsImplementingConditionGroup,
  isPersistedCondition,
} from 'studies/typed/shared/useExperimentDesignState';
import {hideFlowchartElementBeforeLayout} from 'ui-library/reactflow/layout/hideFlowchartElementBeforeLayoutUtils';

import {getActiveAndDownstreamMethodIds, type MethodIdAndName} from './activeMethodUtils';
import {
  createProcedureRunDesignFlowchartAddReplicateNode,
  getProcedureRunDesignFlowchartAddReplicateNodeId,
  ProcedureRunDesignFlowchartAddReplicateNodeData,
} from './ProcedureRunDesignFlowchartAddReplicateNode';
import {
  createProcedureRunDesignFlowchartColumnNode,
  FALLBACK_COLUMN_NODE_HEIGHT,
  ProcedureRunDesignFlowchartColumnNodeData,
} from './ProcedureRunDesignFlowchartColumnNode';
import {ConnectionRole, getNodeConnectionRoles} from './procedureRunDesignFlowchartConnectionUtils';
import {
  makeProcedureRunDesignFlowchartEdge,
  ProcedureRunDesignFlowchartEdgeData,
  ProcedureRunDesignFlowchartEdgeVariant,
} from './ProcedureRunDesignFlowchartEdge';
import {
  createProcedureRunDesignFlowchartGhostNode,
  getProcedureRunDesignFlowchartGhostNodeId,
  ProcedureRunDesignFlowchartGhostNodeData,
} from './ProcedureRunDesignFlowchartGhostNode';
import {
  createProcedureRunDesignFlowchartNode,
  FALLBACK_NODE_WIDTH,
  FALLBACK_NODE_HEIGHT,
  ProcedureRunDesignFlowchartNodeData,
} from './ProcedureRunDesignFlowchartNode';
import {
  getProcedureRunDesignFlowchartNodeId,
  parseProcedureRunDesignFlowchartNodeId,
} from './ProcedureRunDesignFlowchartNodeIdUtils';
import {UnitOperationStatusType} from './procedureRunOverview';

/** Maps a flowchart node id to its method task status. Write and read paths must use matching keys. */
export type StatusByNodeId = ReadonlyMap<string, UnitOperationStatusType>;

/** A directed link between two grid nodes. Both planning and execution edges reduce to this primitive. */
export interface FlowchartEdgeSpec {
  sourceNodeId: string;
  targetNodeId: string;
  /** Defaults to 'default'. */
  variant?: ProcedureRunDesignFlowchartEdgeVariant;
}

export interface GridLayoutOptions {
  horizontalGap: number;
  verticalGap: number;
  startX: number;
  startY: number;
}

/**
 * Creates the initial set of hidden nodes from domain data. This is the single place that maps
 * domain objects (conditions, methods) to ReactFlow nodes. All nodes start with opacity 0% so
 * ReactFlow can measure them before the first visible frame.
 */
export function makeInitialNodes(
  conditionGroups: Array<ConditionGroup>,
  methods: Array<ExperimentMethod>,
  // When false persisted conditions are read-only and render lock icon
  allowEditingPersistedConditions: boolean,
  procedureType: ProcedureType,
  activeMethodId: string | null = null,
  statusByNodeId?: StatusByNodeId,
  // Seeds the edge-dependent showPersistentSourceHandle so the first paint is flicker-free. After that the
  // value is kept live off the rendered edges.
  edgeSpecs: Array<FlowchartEdgeSpec> = []
): {
  columnNodes: Array<Node<ProcedureRunDesignFlowchartColumnNodeData>>;
  rows: Array<Array<Node<ProcedureRunDesignFlowchartNodeData>>>;
} {
  const activeMethodIndex =
    activeMethodId == null ? -1 : methods.findIndex((method) => method.id === activeMethodId);
  const columnNodes = methods.map((method, methodIndex) =>
    hideFlowchartElementBeforeLayout(
      createProcedureRunDesignFlowchartColumnNode({
        methodId: method.id,
        methodName: method.name,
        factorsSet: 0, // TODO(BNCH-153703): Add factors set
        procedureType,
        isActiveMethod: method.id === activeMethodId,
        isHighlighted: false,
        isUpstreamOfActiveMethod: activeMethodIndex > 0 && methodIndex < activeMethodIndex,
      })
    )
  );

  const {roleByNodeId, showPersistentSourceHandleByNodeId} = getNodeConnectionRoles(
    conditionGroups,
    methods,
    edgeSpecs.map((spec) => ({source: spec.sourceNodeId, target: spec.targetNodeId})),
    activeMethodId
  );

  const rows: Array<Array<Node<ProcedureRunDesignFlowchartNodeData>>> = [];
  conditionGroups.forEach((conditionGroup, conditionGroupIndex) => {
    const isLocked = !allowEditingPersistedConditions && isPersistedCondition(conditionGroup);
    // Default to covering all methods when coverage is not specified
    const coveredMethodIds =
      getMethodIdsImplementingConditionGroup(conditionGroup) ?? new Set(methods.map((method) => method.id));
    for (let replicateIndex = 0; replicateIndex < conditionGroup.numReplicates; replicateIndex++) {
      rows.push(
        methods
          .filter((method) => coveredMethodIds.has(method.id))
          .map((method) => {
            const nodeId = getProcedureRunDesignFlowchartNodeId({
              conditionIndex: conditionGroupIndex,
              replicateIndex,
              methodId: method.id,
            });
            return hideFlowchartElementBeforeLayout(
              createProcedureRunDesignFlowchartNode({
                conditionIndex: conditionGroupIndex,
                replicateIndex,
                methodId: method.id,
                conditionName: conditionGroup.name,
                replicateNumber: replicateIndex + 1,
                conditionDescription: conditionGroup.description,
                isHovered: false,
                isHighlighted: false,
                isLocked,
                status: statusByNodeId?.get(nodeId),
                connectionRole: roleByNodeId.get(nodeId) ?? null,
                showPersistentSourceHandle: showPersistentSourceHandleByNodeId.get(nodeId) ?? false,
                isInActiveMethod: method.id === activeMethodId,
              })
            );
          })
      );
    }
  });

  return {columnNodes, rows};
}

/**
 * Maps {@link FlowchartEdgeSpec}s to ReactFlow edges wired to each endpoint's deterministic handles.
 *
 * An edge is marked removable when its source is the upstream column and its target is the active
 * layer, matching the same position that allows drawing. The edge component gates removal on edit mode.
 */
export function makeFlowchartEdges(
  edgeSpecs: Array<FlowchartEdgeSpec>,
  roleByNodeId: ReadonlyMap<string, ConnectionRole> = new Map()
): Array<Edge<ProcedureRunDesignFlowchartEdgeData>> {
  return edgeSpecs.map((spec) => {
    const isRemovable =
      roleByNodeId.get(spec.sourceNodeId) === 'source' && roleByNodeId.get(spec.targetNodeId) === 'target';
    return makeProcedureRunDesignFlowchartEdge(spec, {isRemovable});
  });
}

/** A node in the add-replicate preview row: the add-replicate button or a downstream ghost. */
export type ProcedureRunDesignFlowchartAddReplicateRowNode = Node<
  ProcedureRunDesignFlowchartAddReplicateNodeData | ProcedureRunDesignFlowchartGhostNodeData
>;

/**
 * Builds the add-replicate preview row: the add-replicate button in the active column, an empty ghost
 * in each downstream column, and dashed preview edges chaining button to ghost to ghost in flow order.
 * Nodes start hidden so ReactFlow can measure them before the first visible frame. Callers gate this on
 * edit mode and an active method (split-pool).
 */
export function makeAddReplicateRow({
  methods,
  activeMethodId,
}: {
  methods: Array<ExperimentMethod>;
  activeMethodId: string;
}): {
  row: Array<ProcedureRunDesignFlowchartAddReplicateRowNode>;
  previewEdgeSpecs: Array<FlowchartEdgeSpec>;
} {
  const activeAndDownstreamMethodIds = getActiveAndDownstreamMethodIds(methods, activeMethodId);
  const orderedMethods = methods.filter((method) => activeAndDownstreamMethodIds.has(method.id));

  const row = orderedMethods.map((method) =>
    hideFlowchartElementBeforeLayout(
      method.id === activeMethodId
        ? createProcedureRunDesignFlowchartAddReplicateNode({methodId: method.id})
        : createProcedureRunDesignFlowchartGhostNode({methodId: method.id})
    )
  );

  const syntheticNodeId = (methodId: string): string =>
    methodId === activeMethodId
      ? getProcedureRunDesignFlowchartAddReplicateNodeId(methodId)
      : getProcedureRunDesignFlowchartGhostNodeId(methodId);

  const previewEdgeSpecs: Array<FlowchartEdgeSpec> = [];
  for (let index = 0; index < orderedMethods.length - 1; index++) {
    previewEdgeSpecs.push({
      sourceNodeId: syntheticNodeId(orderedMethods[index].id),
      targetNodeId: syntheticNodeId(orderedMethods[index + 1].id),
      variant: 'preview',
    });
  }

  return {row, previewEdgeSpecs};
}

/**
 * Returns the set of node ids corresponding to the source node and all downstream nodes in the same row.
 */
export function computeSourceAndDownstreamNodeIds(
  sourceNodeId: string,
  conditionGroups: Array<ConditionGroup>,
  methods: Array<MethodIdAndName>
): ReadonlySet<string> {
  const {
    conditionIndex,
    replicateIndex,
    methodId: sourceMethodId,
  } = parseProcedureRunDesignFlowchartNodeId(sourceNodeId);
  const sourceMethodIndex = methods.findIndex((method) => method.id === sourceMethodId);
  if (sourceMethodIndex < 0) {
    return new Set<string>();
  }
  const conditionGroup = conditionGroups[conditionIndex];
  if (conditionGroup == null || replicateIndex >= conditionGroup.numReplicates) {
    return new Set<string>();
  }

  const coveredMethodIds =
    getMethodIdsImplementingConditionGroup(conditionGroup) ?? new Set(methods.map((method) => method.id));
  if (!coveredMethodIds.has(sourceMethodId)) {
    return new Set<string>();
  }

  const nodeIds = new Set<string>();
  for (let methodIndex = sourceMethodIndex; methodIndex < methods.length; methodIndex++) {
    const method = methods[methodIndex];
    if (!coveredMethodIds.has(method.id)) {
      continue;
    }
    nodeIds.add(getProcedureRunDesignFlowchartNodeId({conditionIndex, replicateIndex, methodId: method.id}));
  }
  return nodeIds;
}

/**
 * A directed link between two template methods, identified by their method definition version ids.
 * The planning-time intermediate between the template method graph (`ProcedureEdgeNode`s) and the
 * per-replicate {@link FlowchartEdgeSpec}s.
 */
export interface MethodLink {
  sourceMethodId: string;
  targetMethodId: string;
}

/**
 * Adapts a template flowchart's edges into {@link MethodLink}s, keeping only `METHOD_EDGE`s (the
 * method-to-method execution order) and dropping `MATERIAL_EDGE`s (material flow).
 */
export function methodLinksFromProcedureEdges(edges: Array<ProcedureEdgeNode>): Array<MethodLink> {
  return edges
    .filter((edge) => edge.type === ProcedureEdgeType.METHOD_EDGE)
    .map((edge) => ({sourceMethodId: edge.sourceId, targetMethodId: edge.targetId}));
}

interface EdgeSpecsFromMethodLinksArgs {
  conditionGroups: Array<ConditionGroup>;
  methods: Array<ExperimentMethod>;
  links: Array<MethodLink>;
  shouldIncludeCondition?: (condition: ConditionGroup, conditionIndex: number) => boolean;
  getVariantForLink?: (link: MethodLink) => ProcedureRunDesignFlowchartEdgeVariant;
}

/**
 * Fans out method-level {@link MethodLink}s into per-replicate {@link FlowchartEdgeSpec}s: each link is
 * replicated within every (condition, replicate) row, connecting the two methods' grid nodes on that
 * row. Links whose endpoints aren't among the rendered `methods` are skipped so no edge points at a
 * nonexistent node.
 *
 * `shouldIncludeCondition` optionally filters which condition rows receive edges. This is necessary for
 * when adding conditions to a procedure run, and we want to show edges for unpersisted conditions based on
 * the template procedure's method edges. Links are also filtered by each condition group's coverage map, so
  * no default edge points at an omitted flowchart node.

 */
export function edgeSpecsFromMethodLinks({
  conditionGroups,
  methods,
  links,
  shouldIncludeCondition,
  getVariantForLink,
}: EdgeSpecsFromMethodLinksArgs): Array<FlowchartEdgeSpec> {
  const validMethodIds = new Set(methods.map((method) => method.id));
  const validLinks = links.filter(
    (link) => validMethodIds.has(link.sourceMethodId) && validMethodIds.has(link.targetMethodId)
  );

  const edgeSpecs: Array<FlowchartEdgeSpec> = [];
  conditionGroups.forEach((conditionGroup, conditionGroupIndex) => {
    if (shouldIncludeCondition != null && !shouldIncludeCondition(conditionGroup, conditionGroupIndex)) {
      return;
    }
    const coveredMethodIds = getMethodIdsImplementingConditionGroup(conditionGroup);
    const linksForCondition =
      coveredMethodIds == null
        ? validLinks
        : validLinks.filter(
            (link) => coveredMethodIds.has(link.sourceMethodId) && coveredMethodIds.has(link.targetMethodId)
          );
    for (let replicateIndex = 0; replicateIndex < conditionGroup.numReplicates; replicateIndex++) {
      linksForCondition.forEach((link) => {
        const variant = getVariantForLink?.(link);
        edgeSpecs.push({
          sourceNodeId: getProcedureRunDesignFlowchartNodeId({
            conditionIndex: conditionGroupIndex,
            replicateIndex,
            methodId: link.sourceMethodId,
          }),
          targetNodeId: getProcedureRunDesignFlowchartNodeId({
            conditionIndex: conditionGroupIndex,
            replicateIndex,
            methodId: link.targetMethodId,
          }),
          ...(variant != null ? {variant} : {}),
        });
      });
    }
  });
  return edgeSpecs;
}

/** What's needed to place a PMEI in the design grid: its template method (column) and condition-replicate (row). */
export interface MethodExecutionInstanceInfoForPlacement {
  pmeiId: string;
  methodId: string;
  conditionId: string;
  replicateNumber: number;
}

/**
 * A directed edge in a `ProcedureRun`'s runtime condition graph, from a source PMEI to a target PMEI.
 */
export interface ConditionReplicateEdge {
  sourcePmeiId: string;
  targetPmeiId: string;
}

/** A PMEI resolved to the grid node that renders it, plus its template method ID (for downstream styling). */
export interface ResolvedPmeiNode {
  nodeId: string;
  methodId: string;
}

/**
 * Maps each persisted `ProcedureRunCondition` id to its rendered condition group index (the grid row).
 * A condition id belongs to exactly one group. Shared by the status map and edge resolver.
 */
export function buildConditionGroupIndexByConditionId(
  conditions: Array<ConditionGroup>
): ReadonlyMap<string, number> {
  return new Map<string, number>(
    conditions.flatMap((conditionGroup, index) =>
      Object.values(conditionGroup.conditionIdsByPmdvId)
        .filter((conditionId): conditionId is string => conditionId != null)
        .map((conditionId) => [conditionId, index])
    )
  );
}

/**
 * Maps each PMEI id to the grid node that renders it, skipping PMEIs whose condition isn't rendered.
 * Asserts the persisted `replicateNumber` is within the rendered group's `numReplicates`: today the live
 * design can't drop a persisted replicate, so an out-of-range value is a data bug, not an edit.
 */
export function buildPmeiToNodeIdMap(
  infoForPlacements: Array<MethodExecutionInstanceInfoForPlacement>,
  conditions: Array<ConditionGroup>
): ReadonlyMap<string, ResolvedPmeiNode> {
  const conditionIndexById = buildConditionGroupIndexByConditionId(conditions);
  const numReplicatesByConditionId = new Map<string, number>(
    conditions.flatMap((conditionGroup) =>
      Object.values(conditionGroup.conditionIdsByPmdvId)
        .filter((conditionId): conditionId is string => conditionId != null)
        .map((conditionId): [string, number] => [conditionId, conditionGroup.numReplicates])
    )
  );
  const pmeiToNode = new Map<string, ResolvedPmeiNode>();
  infoForPlacements.forEach(({pmeiId, methodId, conditionId, replicateNumber}) => {
    const conditionIndex = conditionIndexById.get(conditionId);
    if (conditionIndex == null) {
      return;
    }
    const numReplicates = numReplicatesByConditionId.get(conditionId);
    assert(
      numReplicates != null && replicateNumber >= 1 && replicateNumber <= numReplicates,
      `PMEI ${pmeiId} has replicateNumber ${replicateNumber} outside the rendered range [1, ${numReplicates}] for condition ${conditionId}`
    );
    pmeiToNode.set(pmeiId, {
      nodeId: getProcedureRunDesignFlowchartNodeId({
        conditionIndex,
        replicateIndex: replicateNumber - 1,
        methodId,
      }),
      methodId,
    });
  });
  return pmeiToNode;
}

/**
 * Builds an inverse lookup from buildPmeiToNodeIdMap for referencing PMEI ids by flowchart node id (for
 * example in constructing save payloads). Expects each rendered, persisted node to resolve to at most one PMEI.
 */
export function buildNodeIdToPmeiIdMap(
  infoForPlacements: Array<MethodExecutionInstanceInfoForPlacement>,
  conditions: Array<ConditionGroup>
): ReadonlyMap<string, string> {
  const pmeiToNode = buildPmeiToNodeIdMap(infoForPlacements, conditions);
  const nodeIdToPmeiId = new Map<string, string>();
  pmeiToNode.forEach(({nodeId}, pmeiId) => {
    assert(!nodeIdToPmeiId.has(nodeId), `Multiple PMEIs resolved to the same flowchart node ${nodeId}`);
    nodeIdToPmeiId.set(nodeId, pmeiId);
  });
  return nodeIdToPmeiId;
}

/** Options for `downstream` tinting relative to the active unit-op layer. */
export interface ExecutionEdgeVariantOptions {
  /** Ordered unit-op methods; used to compare the source method against the active one. */
  methods?: Array<MethodIdAndName>;
  /** Active method id, or `null` when there's no active layer. */
  activeMethodId?: string | null;
}

/**
 * Maps a runtime condition graph to {@link FlowchartEdgeSpec}s, skipping edges with an unresolved
 * endpoint. With `activeMethodId`, edges whose source is at or after the active method get
 * `downstream`; others stay `default`.
 */
export function executionEdgeSpecsFromConditionGraph(
  edges: Array<ConditionReplicateEdge>,
  pmeiToNode: ReadonlyMap<string, ResolvedPmeiNode>,
  {methods = [], activeMethodId = null}: ExecutionEdgeVariantOptions = {}
): Array<FlowchartEdgeSpec> {
  const methodIndexById = new Map(methods.map((method, index) => [method.id, index]));
  let activeMethodIndex = -1;
  if (activeMethodId != null) {
    const index = methodIndexById.get(activeMethodId);
    assert(index != null, `Active method ${activeMethodId} is not among the provided methods`);
    activeMethodIndex = index;
  }

  const edgeSpecs: Array<FlowchartEdgeSpec> = [];
  edges.forEach((edge) => {
    const sourceNode = pmeiToNode.get(edge.sourcePmeiId);
    const targetNode = pmeiToNode.get(edge.targetPmeiId);
    if (sourceNode == null || targetNode == null) {
      return;
    }
    edgeSpecs.push({
      sourceNodeId: sourceNode.nodeId,
      targetNodeId: targetNode.nodeId,
      variant: isDownstreamOfActiveLayer(sourceNode.methodId, methodIndexById, activeMethodIndex)
        ? 'downstream'
        : 'default',
    });
  });
  return edgeSpecs;
}

/** True when `sourceMethodId` is at or after the active method in flow order. */
function isDownstreamOfActiveLayer(
  sourceMethodId: string,
  methodIndexById: ReadonlyMap<string, number>,
  activeMethodIndex: number
): boolean {
  if (activeMethodIndex < 0) {
    return false;
  }
  const sourceMethodIndex = methodIndexById.get(sourceMethodId);
  assert(sourceMethodIndex != null, `Source method ${sourceMethodId} is not among the provided methods`);
  return sourceMethodIndex >= activeMethodIndex;
}

/**
 * Returns the largest measured height among the given nodes, falling back to `fallbackHeight`
 * when none of them have been measured yet (height null/0). Used to position grid rows below the
 * column headers using their real rendered height rather than a static estimate.
 */
export function getMaxMeasuredNodeHeight<T>(nodes: Array<Node<T>>, fallbackHeight: number): number {
  const measuredHeights = nodes.map((node) => node.height ?? 0).filter((height) => height > 0);
  return measuredHeights.length > 0 ? Math.max(...measuredHeights) : fallbackHeight;
}

/**
 * Purely geometric grid layout. Takes measured node dimensions from ReactFlow and computes
 * absolute positions for each node. Returns repositioned rows and per-column x/width values
 * for use by recomputeColumnPositions.
 *
 * Rows may be sparse: a row need not contain a node for every column. Each node is placed in its
 * own column (resolved via `getColumnIndex`), so a method with no cell in a given row leaves an
 * aligned gap rather than shifting later cells left. `columnCount` is the total number of columns
 * (methods), so columns with no cell in any row still reserve their slot.
 *
 * Present nodes must have width/height populated by ReactFlow before calling this.
 */
export function recomputeGridPositions<T>(
  rows: Array<Array<Node<T>>>,
  columnCount: number,
  getColumnIndex: (node: Node<T>) => number,
  {horizontalGap, verticalGap, startX, startY}: GridLayoutOptions
): {
  repositionedRows: Array<Array<Node<T>>>;
  columnDimensions: Array<{x: number; width: number}>;
} {
  if (rows.length === 0 || columnCount === 0) {
    return {repositionedRows: rows, columnDimensions: []};
  }

  // Each column is as wide as its widest cell across all rows. Columns with no cell keep the
  // fallback width so they still reserve a slot and stay aligned (leaving a visible gap).
  const columnWidths = Array.from({length: columnCount}, () => FALLBACK_NODE_WIDTH);
  rows.forEach((row) => {
    row.forEach((node) => {
      const columnIndex = getColumnIndex(node);
      assertValidColumnIndex(node.id, columnIndex, columnCount);
      columnWidths[columnIndex] = Math.max(columnWidths[columnIndex], node.width ?? FALLBACK_NODE_WIDTH);
    });
  });
  const columnDimensions = computeColumnDimensions(columnWidths, {startX, horizontalGap});

  // Reposition rows with cumulative Y, using the tallest node in each row.
  let currentY = startY;
  const repositionedRows = rows.map((row) => {
    const rowHeight =
      row.length > 0
        ? Math.max(
            ...row.map((node) =>
              node.height != null && node.height > 0 ? node.height : FALLBACK_NODE_HEIGHT
            )
          )
        : FALLBACK_NODE_HEIGHT;
    const repositionedRow = row.map((node) => {
      const columnIndex = getColumnIndex(node);
      assertValidColumnIndex(node.id, columnIndex, columnCount);
      return {
        ...node,
        position: {x: columnDimensions[columnIndex].x, y: currentY},
      };
    });
    currentY += rowHeight + verticalGap;
    return repositionedRow;
  });

  return {repositionedRows, columnDimensions};
}

function assertValidColumnIndex(nodeId: string, columnIndex: number, columnCount: number): void {
  assert(
    Number.isInteger(columnIndex) && columnIndex >= 0 && columnIndex < columnCount,
    `Node ${nodeId} resolved to invalid column index ${String(columnIndex)}. Expected an integer in [0, ${columnCount - 1}].`
  );
}

/**
 * Lays out the given column widths left-to-right, advancing x by `width + horizontalGap` per column.
 * Single source of truth for column x-spacing, using the widths from the widest grid cell per column.
 */
function computeColumnDimensions(
  widths: Array<number>,
  {startX, horizontalGap}: {startX: number; horizontalGap: number}
): Array<{x: number; width: number}> {
  let currentX = startX;
  return widths.map((width) => {
    const dimension = {x: currentX, width};
    currentX += width + horizontalGap;
    return dimension;
  });
}

/**
 * Positions and sizes column overlay nodes using the column dimensions computed by
 * recomputeGridPositions. Width matches the widest node in each column; height is that column's own
 * height so columns without a cell in a lower band (e.g. the add-replicate row) stop early.
 */
export function recomputeColumnPositions<T>(
  columnNodes: Array<Node<T>>,
  columnDimensions: Array<{x: number; width: number}>,
  {startY, columnHeights}: {startY: number; columnHeights: Array<number>}
): Array<Node<T>> {
  assert(
    columnNodes.length === columnDimensions.length,
    'columnNodes and columnDimensions must have the same length'
  );
  assert(
    columnNodes.length === columnHeights.length,
    'columnNodes and columnHeights must have the same length'
  );

  return columnNodes.map((node, index) => {
    const dimension = columnDimensions[index];
    return {
      ...node,
      position: {x: dimension.x, y: startY},
      style: {...node.style, width: dimension.width, height: columnHeights[index]},
    };
  });
}

/**
 * Per-column overlay heights from the column top (columnStartY) through each column's own lowest cell.
 * A column with no cell in a lower band stops at its last real cell instead of matching the tallest
 * column, so the add-replicate row only extends the active and downstream columns that contain it.
 */
export function computeColumnHeights<T>(
  repositionedRows: Array<Array<Node<T>>>,
  columnCount: number,
  getColumnIndex: (node: Node<T>) => number,
  columnStartY: number
): Array<number> {
  const heights = Array.from({length: columnCount}, () => FALLBACK_COLUMN_NODE_HEIGHT);
  const columnHasCell = Array.from({length: columnCount}, () => false);
  repositionedRows.forEach((row) => {
    row.forEach((node) => {
      const columnIndex = getColumnIndex(node);
      assertValidColumnIndex(node.id, columnIndex, columnCount);
      const nodeHeight = node.height != null && node.height > 0 ? node.height : FALLBACK_NODE_HEIGHT;
      const bottom = node.position.y + nodeHeight - columnStartY;
      if (!columnHasCell[columnIndex] || bottom > heights[columnIndex]) {
        heights[columnIndex] = bottom;
        columnHasCell[columnIndex] = true;
      }
    });
  });
  return heights;
}
